import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  CircularProgress,
  makeStyles,
  Popover,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  LinearProgress,
} from '@material-ui/core';
import SyncIcon from '@material-ui/icons/Sync';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  Header,
  Page,
  Content,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  deployMetricsApiRef,
  ProjectInfo,
  SyncStatus,
} from '../api/DeployMetricsApi';
import { useDeployMetrics } from '../hooks/useDeployMetrics';
import { useDevopsAccess } from '../../../hooks/useDevopsAccess';
import { MetricsSummaryCard } from './MetricsSummaryCard';
import { EnvironmentBreakdownCard } from './EnvironmentBreakdownCard';
import { TopProjectsCard } from './TopProjectsCard';
import { MonthlyTrendChart } from './MonthlyTrendChart';
import { EnvironmentPieChart } from './EnvironmentPieChart';
import {
  MetricsFilterPopover,
  MetricsFilters,
  DEFAULT_FILTERS,
} from './MetricsFilterPopover';

const POLL_INTERVAL_MS = 3000;

const useStyles = makeStyles(theme => ({
  filters: {
    padding: theme.spacing(2, 0, 1),
  },
  filterControl: {
    minWidth: 200,
  },
  syncPopover: {
    padding: theme.spacing(2),
    minWidth: 260,
  },
  progressBox: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
}));

const phaseLabels: Record<string, string> = {
  idle: 'Aguardando',
  resetting: 'Resetando métricas…',
  discovering: 'Descobrindo projetos…',
  syncing: 'Sincronizando pipelines…',
  done: 'Concluído',
  error: 'Erro na sincronização',
};

export const DeployMetricsPage = () => {
  const classes = useStyles();
  const api = useApi(deployMetricsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { allowed: isDevops } = useDevopsAccess();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [ref, setRef] = useState('');
  const [tribo, setTribo] = useState('');
  const [tribos, setTribos] = useState<Array<{ ref: string; label: string }>>([]);
  const [metricsFilters, setMetricsFilters] = useState<MetricsFilters>(DEFAULT_FILTERS);
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncVersion, setSyncVersion] = useState(0);
  const [syncAnchorEl, setSyncAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  );
  const [resetMetrics, setResetMetrics] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showProgress, setShowProgress] = useState(false);

  useEffect(() => {
    api.getProjects(tribo || undefined).then(setProjects).catch(() => {});
    // Reset project selection when tribo changes
    setProjectId('');
  }, [api, syncVersion, tribo]);

  // Fetch team groups from catalog for the tribo filter
  useEffect(() => {
    catalogApi
      .getEntities({
        filter: { kind: 'Group', 'spec.type': 'team' },
        fields: ['metadata.name', 'metadata.namespace', 'metadata.description'],
      })
      .then(response => {
        const opts = response.items
          .map(entity => {
            const ns = entity.metadata.namespace ?? 'default';
            const name = entity.metadata.name;
            const label = (entity.metadata as any).description || name;
            return { ref: `group:${ns}/${name}`, label };
          })
          .sort((a, b) => a.label.localeCompare(b.label));
        setTribos(opts);
      })
      .catch(() => {});
  }, [catalogApi]);

  // Poll sync status while syncing
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    setShowProgress(true);
    if (dismissRef.current) {
      clearTimeout(dismissRef.current);
      dismissRef.current = null;
    }
    const poll = async () => {
      try {
        const status = await api.getSyncStatus();
        setSyncStatus(status);
        if (!status.syncing) {
          // Sync finished — stop polling and refresh data
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          setSyncing(false);
          setSyncVersion(v => v + 1);
          // Auto-dismiss progress bar after 5 seconds
          dismissRef.current = setTimeout(() => {
            setShowProgress(false);
            dismissRef.current = null;
          }, 5000);
        }
      } catch {
        // ignore polling errors
      }
    };
    // Immediate first poll
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, [api]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      if (dismissRef.current) {
        clearTimeout(dismissRef.current);
        dismissRef.current = null;
      }
    };
  }, []);

  // Check if a sync is already running on mount
  useEffect(() => {
    api
      .getSyncStatus()
      .then(status => {
        if (status.syncing) {
          setSyncing(true);
          setSyncStatus(status);
          startPolling();
        }
        // Don't show stale "done" status on page load — the progress bar
        // should only appear while a sync is actively running or was
        // triggered during this session.
      })
      .catch(() => {});
  }, [api, startPolling]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncAnchorEl(null);
    setSyncStatus(null);
    try {
      await api.triggerSync({ reset: resetMetrics });
      startPolling();
    } catch {
      setSyncing(false);
    } finally {
      setResetMetrics(false);
    }
  }, [api, resetMetrics, startPolling]);

  const query = useMemo(() => {
    // Compute effective date range from period filter or manual dates
    let effectiveStartDate = startDate;
    let effectiveEndDate = endDate;

    if (metricsFilters.periodDays !== null) {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - metricsFilters.periodDays);
      effectiveStartDate = start.toISOString().split('T')[0];
      effectiveEndDate = now.toISOString().split('T')[0];
    }

    // Build environments filter
    const environments: string[] = [];
    if (metricsFilters.showPrd) environments.push('production');
    if (metricsFilters.showHml) environments.push('hml');
    if (metricsFilters.showDev) environments.push('development');

    return {
      ...(projectId !== '' ? { projectId: Number(projectId) } : {}),
      ...(effectiveStartDate ? { startDate: effectiveStartDate } : {}),
      ...(effectiveEndDate ? { endDate: effectiveEndDate } : {}),
      ...(ref ? { ref } : {}),
      // Only send environments filter if not all are selected
      ...(!metricsFilters.showPrd || !metricsFilters.showHml || !metricsFilters.showDev
        ? { environments }
        : {}),
      // Exclude devops projects unless checkbox is checked.
      // When a specific tribo is selected via the tribo filter, skip the exclude
      // (the includeOwner already narrows the results to that tribo only).
      ...(!metricsFilters.showDevopsProjects && !tribo
        ? { excludeOwners: ['group:default/tribo-devops', 'group:default/devops'] }
        : {}),
      // Include only projects from selected tribo
      ...(tribo ? { includeOwner: tribo } : {}),
    };
  }, [projectId, startDate, endDate, ref, metricsFilters, tribo]);

  const { data, loading, error, retrying, retry } = useDeployMetrics(query, syncVersion);

  // Auto-retry when owner filter was requested but not applied (cache still loading)
  const ownerFilterPending = !metricsFilters.showDevopsProjects && data?.ownerFilterApplied === false;
  const ownerRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (ownerFilterPending && !loading && !retrying) {
      ownerRetryRef.current = setTimeout(() => {
        retry();
      }, 5000);
    }
    return () => {
      if (ownerRetryRef.current) {
        clearTimeout(ownerRetryRef.current);
        ownerRetryRef.current = null;
      }
    };
  }, [ownerFilterPending, loading, retrying, retry]);

  const progressPercent =
    syncStatus && syncStatus.totalProjects > 0
      ? Math.round(
          (syncStatus.completedProjects / syncStatus.totalProjects) * 100,
        )
      : 0;

  return (
    <Page themeId="tool">
      <Header title="Métricas" subtitle="Visão consolidada de deployments" />
      <Content>
        {/* Filters + Sync */}
        <div className={classes.filters}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <TextField
                label="Data Início"
                type="date"
                variant="outlined"
                size="small"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'filter-start-date' }}
              />
            </Grid>
            <Grid item>
              <TextField
                label="Data Fim"
                type="date"
                variant="outlined"
                size="small"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'filter-end-date' }}
              />
            </Grid>
            <Grid item>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.filterControl}
              >
                <InputLabel id="project-filter-label">Projeto</InputLabel>
                <Select
                  labelId="project-filter-label"
                  label="Projeto"
                  value={projectId}
                  onChange={e => setProjectId(e.target.value as number | '')}
                  data-testid="filter-project"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {projects.map(p => (
                    <MenuItem key={p.projectId} value={p.projectId}>
                      {p.projectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.filterControl}
              >
                <InputLabel id="tribo-filter-label">Tribo</InputLabel>
                <Select
                  labelId="tribo-filter-label"
                  label="Tribo"
                  value={tribo}
                  onChange={e => setTribo(e.target.value as string)}
                  data-testid="filter-tribo"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {tribos.map(t => (
                    <MenuItem key={t.ref} value={t.ref}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <TextField
                label="Branch / Tag"
                variant="outlined"
                size="small"
                value={ref}
                onChange={e => setRef(e.target.value)}
                inputProps={{ 'data-testid': 'filter-ref' }}
              />
            </Grid>
            <Grid item>
              <MetricsFilterPopover
                filters={metricsFilters}
                onChange={setMetricsFilters}
              />
            </Grid>
            {isDevops && (
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    syncing ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SyncIcon />
                    )
                  }
                  onClick={e => setSyncAnchorEl(e.currentTarget)}
                  disabled={syncing}
                  data-testid="sync-button"
                >
                  Sync
                </Button>
                <Popover
                  open={Boolean(syncAnchorEl)}
                  anchorEl={syncAnchorEl}
                  onClose={() => {
                    setSyncAnchorEl(null);
                    setResetMetrics(false);
                  }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <Box className={classes.syncPopover}>
                    <Typography variant="subtitle2" gutterBottom>
                      Opções de Sincronização
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={resetMetrics}
                          onChange={e => setResetMetrics(e.target.checked)}
                          color="primary"
                          data-testid="reset-metrics-checkbox"
                        />
                      }
                      label="Resetar Métricas"
                    />
                    <Box mt={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SyncIcon />}
                        onClick={handleSync}
                        data-testid="confirm-sync-button"
                      >
                        Executar Sync
                      </Button>
                    </Box>
                  </Box>
                </Popover>
              </Grid>
            )}
          </Grid>
        </div>

        {/* Sync progress bar */}
        {showProgress && syncStatus && syncStatus.phase !== 'idle' && (
          <Box className={classes.progressBox} data-testid="sync-progress">
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="body2">
                  {phaseLabels[syncStatus.phase] ?? syncStatus.phase}
                  {syncStatus.currentProject &&
                    syncStatus.phase === 'syncing' &&
                    ` — ${syncStatus.currentProject}`}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="textSecondary">
                  {syncStatus.completedProjects}/{syncStatus.totalProjects}{' '}
                  projetos · {syncStatus.totalPipelines} pipelines
                </Typography>
              </Grid>
            </Grid>
            <LinearProgress
              variant={
                syncStatus.syncing && syncStatus.totalProjects === 0
                  ? 'indeterminate'
                  : 'determinate'
              }
              value={syncStatus.phase === 'done' ? 100 : progressPercent}
              className={classes.progressBar}
              color={syncStatus.phase === 'error' ? 'secondary' : 'primary'}
            />
            {syncStatus.error && (
              <Typography variant="caption" color="error">
                {syncStatus.error}
              </Typography>
            )}
          </Box>
        )}

        {/* Content */}
        {loading && <Progress />}
        {ownerFilterPending && !loading && (
          <Box display="flex" alignItems="center" justifyContent="center" py={1} data-testid="owner-filter-pending">
            <CircularProgress size={16} />
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
              Carregando filtro de projetos DevOps…
            </Typography>
          </Box>
        )}
        {retrying && (
          <Box display="flex" alignItems="center" justifyContent="center" py={4} data-testid="retry-indicator">
            <CircularProgress size={24} />
            <Typography variant="body2" color="textSecondary" style={{ marginLeft: 12 }}>
              Tentando novamente…
            </Typography>
          </Box>
        )}
        {error && !retrying && (
          <Box>
            <ResponseErrorPanel error={error} />
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={retry}
                data-testid="retry-button"
              >
                Tentar novamente
              </Button>
            </Box>
          </Box>
        )}
        {!loading && !error && !retrying && (
          <Grid container spacing={3}>
            {/* Row 1: Summary cards */}
            <Grid item xs={12} md={4}>
              <MetricsSummaryCard
                totalDeployments={data?.totalDeployments ?? 0}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <EnvironmentBreakdownCard
                byEnvironment={data?.byEnvironment ?? {}}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TopProjectsCard topProjects={data?.topProjects ?? []} />
            </Grid>
            {/* Row 2: Charts */}
            <Grid item xs={12} md={8}>
              <MonthlyTrendChart data={data?.monthlyTrend ?? []} />
            </Grid>
            <Grid item xs={12} md={4}>
              <EnvironmentPieChart
                byEnvironment={data?.byEnvironment ?? {}}
              />
            </Grid>
          </Grid>
        )}
      </Content>
    </Page>
  );
};
